import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Dialog,
  Icon,
  TextField,
  Layout,
  SpinnerWheel,
  Button,
  Banner,
  RadioGroup,
  Radio,
} from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { formatEmployeeName } from '../../../presenters/employeesPresenter'
import { useAssignDriver } from '../../hooks/useAssignDriver'
import { useGetCarrierEmployees } from '../../hooks/useGetCarrierEmployees'

type DriversSelectionProps = {
  readonly carrierUUID: string
  readonly shipmentUUID: string
  readonly driverUUID?: string
  readonly onCancel: () => void
  readonly onAddNewDriver: () => void
  readonly onDriverAssigned: () => void
}

export function DriverSelection({
  carrierUUID,
  driverUUID,
  shipmentUUID,
  onCancel,
  onAddNewDriver,
  onDriverAssigned,
}: DriversSelectionProps) {
  const { employees, requestStatus, fetchEmployees } = useGetCarrierEmployees(carrierUUID)
  const { patchAssignDriver, requestStatus: saveStatus } = useAssignDriver()
  const [selectedDriver, setSelectedDriver] = useState(driverUUID)
  const [search, setSearch] = useState('')

  const filteredEmployees = useMemo(() => {
    return employees.filter(({ name, phoneNumber }) =>
      (name ?? phoneNumber).toLowerCase().includes(search.toLowerCase())
    )
  }, [employees, search])

  useEffect(() => {
    if (saveStatus === RequestStatus.Done) {
      onDriverAssigned()
    }
  }, [onDriverAssigned, saveStatus])

  useEffect(() => {
    if (saveStatus === RequestStatus.Error) {
      toast.error('Failed to assign the driver')
    }
  }, [onDriverAssigned, saveStatus])

  async function handleSave() {
    if (!selectedDriver) return

    await patchAssignDriver(selectedDriver, shipmentUUID)
  }

  return (
    <>
      <Dialog.Body>
        <Layout.Stack gap="spacing-4">
          {driverUUID && (
            <Banner>
              <Banner.Title>Before you change</Banner.Title>
              <Banner.Description>
                Changing the driver will affect the current tracking and the new info will be sent
                to the shipper.
              </Banner.Description>
            </Banner>
          )}

          <TextField
            type="search"
            data-testid="search-driver"
            placeholder="Search for a driver"
            leading={<Icon name="search" size="16px" color="color-text-tertiary" />}
            onInput={event => {
              setSearch((event.target as HTMLInputElement).value)
            }}
            onClear={() => setSearch('')}
          />

          {requestStatus === RequestStatus.Pending && (
            <Layout.Group align="center" justify="center" style={{ height: '100%' }}>
              <SpinnerWheel size="32px" title="Loading..." />
            </Layout.Group>
          )}

          {requestStatus === RequestStatus.Error && (
            <Banner variant="danger">
              <Banner.Title>Failed to load the drivers</Banner.Title>
              <Banner.Actions>
                <Banner.ActionPrimary onClick={() => fetchEmployees(carrierUUID)}>
                  Retry
                </Banner.ActionPrimary>
              </Banner.Actions>
            </Banner>
          )}

          {requestStatus === RequestStatus.Done && (
            <>
              {filteredEmployees.length === 0 && (
                <Banner variant="warning">
                  <Banner.Title>No drivers found</Banner.Title>
                </Banner>
              )}

              <RadioGroup
                value={selectedDriver}
                onChange={event => setSelectedDriver(event.detail.value)}
              >
                {filteredEmployees.map(employee => (
                  <Radio key={employee.uuid} value={employee.uuid}>
                    {formatEmployeeName(employee)}
                  </Radio>
                ))}
              </RadioGroup>

              <Button
                style={{ alignSelf: 'flex-start' }}
                variant="tertiary"
                size="small"
                leading={<Icon name="plus" size="12px" color="color-primary-100" />}
                onClick={onAddNewDriver}
              >
                Add new driver
              </Button>
            </>
          )}
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={onCancel}>Cancel</Dialog.ActionTertiary>
        <Dialog.ActionPrimary onClick={handleSave}>Confirm</Dialog.ActionPrimary>
      </Dialog.Actions>
    </>
  )
}
